import { useEffect, useRef, useState } from "react";

import { INSIGHT_TYPE } from "@/constants";
import { useEpisode } from "@/containers/Episode/context";
import { useLocalAudio } from "@/containers/Episode/LocalAudioContext";
import useHash from "@/hooks/useHash";
import { cx } from "@/utils";
import UndoRemovedAll from "@components/UndoRemovedAll";

import styles from "./EpisodeBase.module.css";

// This is the base for the Desktop episode, it has some elements that all of the Episodes share.
type EpisodeBaseProps = {
  className?: string;
  children?: React.ReactElement;
};

const HIGHLIGHT_DURATION = 3500;

const EpisodeBase: React.FunctionComponent<EpisodeBaseProps> = ({
  className,
  children,
}) => {
  const { playing } = useLocalAudio({ playing: true });
  const hash = useHash();

  const [highlight, setHighlight] = useState(false);
  const episode = useEpisode();
  const {
    mentions,
    episode_guid,
    source,
    title_mention_uuid,
    description_mention_uuid,
  } = episode;

  const episodeRef = useRef<HTMLDivElement>(null);

  // If a hash is present in the URL, focuses the matching mention
  useEffect(() => {
    const mention_uuids: (string | null)[] =
      mentions?.map((m) => m.public_id) || [];
    // adding meta-hits to uuids-array, if any.
    mention_uuids.push(title_mention_uuid, description_mention_uuid);

    if (mention_uuids.includes(hash) && episodeRef.current) {
      // Offset scrolling by a timeout to allow the page to render first.
      // Does not work in chrome without it.
      setTimeout(() => {
        if (episodeRef.current) {
          episodeRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 50);

      setHighlight(true);

      const t = setTimeout(() => {
        setHighlight(false);
      }, HIGHLIGHT_DURATION);

      return () => clearTimeout(t);
    }
  }, [hash, description_mention_uuid, title_mention_uuid, mentions]);

  const isHighreachmention = episode.insights?.includes(
    INSIGHT_TYPE.HIGH_REACH_MENTION
  );
  const { removedSource, removedMentionIds } = useEpisode();
  const allMentionsRemoved = removedMentionIds.length === mentions.length;

  return (
    <div
      ref={episodeRef}
      key={`episode-${episode_guid}`}
      id={`episode-${episode_guid}`}
      data-episode-id={`episode-${episode_guid}`}
      className={cx(
        className,
        styles.root,
        "episode", // for tests only.
        "episode-container", // for tests only. Should be moved to data-testid, but is used in 50 places right now ...
        `type-${source}`,
        {
          [styles.playing]: playing,
          [styles.highlighted]: highlight,
          [styles.highreachmention]: isHighreachmention,
        }
      )}
      data-testid="episode-container"
    >
      {children}
      {removedSource || allMentionsRemoved ? <UndoRemovedAll /> : null}
    </div>
  );
};
export default EpisodeBase;
